// src/components/notifications/NotificationsProvider.js
/**
 * NOTIFICATIONS PROVIDER (READ-SIDE)
 * -----------------------------------------------------------------------------
 * This provider gives your app ONE real-time listener for notifications, so you
 * don’t add Firestore watchers all over the place.
 *
 * Reads from:
 *   ✅ users/{currentUserId}/notifications
 *
 * Provides:
 * - notifications (latest 50)
 * - unreadCount
 * - markAsRead / markAllAsRead
 * - deleteOne / clearAll
 * - requireNotificationSeen (company feature flag)
 *
 * Retention:
 * - Your write-side (src/utils/notifications.js) sets expireAt = now + 30 days.
 * - If you enable Firestore TTL for collection group "notifications" and field
 *   "expireAt", Firestore will permanently delete old notifications automatically. :contentReference[oaicite:2]{index=2}
 */

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../firebase";

const NotificationsContext = createContext(null);

export const NotificationsProvider = ({ currentUserId, companyId, children }) => {
  const [uid, setUid] = useState(currentUserId || null);
  const [resolvedCompanyId, setResolvedCompanyId] = useState(companyId || null);

  const [requireNotificationSeen, setRequireNotificationSeen] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // If caller didn’t pass uid, derive from Firebase Auth
  useEffect(() => {
    if (currentUserId) return;

    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || null);
    });

    return () => unsub();
  }, [currentUserId]);

  // Resolve companyId if not provided (from user profile)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (companyId) {
        setResolvedCompanyId(companyId);
        return;
      }
      if (!uid) {
        setResolvedCompanyId(null);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", uid));
        const cid = snap.exists() ? snap.data()?.companyId || null : null;
        if (!cancelled) setResolvedCompanyId(cid);
      } catch (e) {
        console.warn("[NotificationsProvider] Could not load companyId:", e);
        if (!cancelled) setResolvedCompanyId(null);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [uid, companyId]);

  // Load requireNotificationSeen flag (company feature flag)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!resolvedCompanyId) {
        setRequireNotificationSeen(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "companies", resolvedCompanyId));
        const flags = snap.exists() ? snap.data()?.featureFlags || {} : {};
        if (!cancelled) setRequireNotificationSeen(!!flags.requireNotificationSeen);
      } catch (e) {
        console.warn("[NotificationsProvider] Could not load feature flags:", e);
        if (!cancelled) setRequireNotificationSeen(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [resolvedCompanyId]);

  // ✅ Single live listener for the entire app
  useEffect(() => {
    if (!uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const ref = collection(db, "users", uid, "notifications");
    const q = query(ref, orderBy("createdAt", "desc"), limit(50));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();

        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Optional: hide already-expired docs immediately (TTL deletes may lag)
          .filter((n) => {
            const exp = n?.expireAt;
            const expMs =
              typeof exp?.toMillis === "function"
                ? exp.toMillis()
                : typeof exp?.seconds === "number"
                ? exp.seconds * 1000
                : null;
            if (typeof expMs === "number" && expMs <= now) return false;
            return true;
          });

        setNotifications(items);
        setLoading(false);
      },
      (err) => {
        console.error("[NotificationsProvider] Error loading notifications:", err);
        setNotifications([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);

  const markAsRead = useCallback(
    async (id) => {
      if (!uid || !id) return;
      try {
        await updateDoc(doc(db, "users", uid, "notifications", id), {
          isRead: true,
          readAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        console.error("[NotificationsProvider] markAsRead failed:", e);
      }
    },
    [uid]
  );

  const markAllAsRead = useCallback(async () => {
    if (!uid) return;
    const unread = notifications.filter((n) => !n.isRead);
    if (unread.length === 0) return;

    try {
      const batch = writeBatch(db);
      unread.forEach((n) => {
        batch.update(doc(db, "users", uid, "notifications", n.id), {
          isRead: true,
          readAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
    } catch (e) {
      console.error("[NotificationsProvider] markAllAsRead failed:", e);
    }
  }, [uid, notifications]);

  const deleteOne = useCallback(
    async (id) => {
      if (!uid || !id) return;
      try {
        await deleteDoc(doc(db, "users", uid, "notifications", id));
      } catch (e) {
        console.error("[NotificationsProvider] deleteOne failed:", e);
      }
    },
    [uid]
  );

  const clearAll = useCallback(async () => {
    if (!uid) return;
    try {
      await Promise.all(notifications.map((n) => deleteDoc(doc(db, "users", uid, "notifications", n.id))));
    } catch (e) {
      console.error("[NotificationsProvider] clearAll failed:", e);
    }
  }, [uid, notifications]);

  const value = useMemo(
    () => ({
      currentUserId: uid,
      companyId: resolvedCompanyId,
      requireNotificationSeen,
      notifications,
      loading,
      unreadCount,
      markAsRead,
      markAllAsRead,
      deleteOne,
      clearAll,

      // helpful for UI text
      retentionDays: 30,
    }),
    [
      uid,
      resolvedCompanyId,
      requireNotificationSeen,
      notifications,
      loading,
      unreadCount,
      markAsRead,
      markAllAsRead,
      deleteOne,
      clearAll,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used inside <NotificationsProvider />");
  return ctx;
};
